import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Car, Plus, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const VehicleSelect = () => {
  const { locale } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newVehicle, setNewVehicle] = useState({
    brand: '',
    model: '',
    engine: '',
    year: new Date().getFullYear(),
    vin: '',
    license_plate: ''
  });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`);
      setVehicles(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Fahrzeuge');
    } finally {
      setLoading(false);
    }
  };

  const handleAddVehicle = async () => {
    try {
      await axios.post(`${API}/vehicles`, newVehicle);
      toast.success('Fahrzeug hinzugefügt');
      setShowAddDialog(false);
      setNewVehicle({ brand: '', model: '', engine: '', year: new Date().getFullYear(), vin: '', license_plate: '' });
      fetchVehicles();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleSelectVehicle = (vehicleId) => {
    navigate(`/diagnosis/start?vehicleId=${vehicleId}`);
  };

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold mb-2" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
              Fahrzeug auswählen
            </h2>
            <p style={{ color: '#94A3B8' }}>Wählen Sie ein Fahrzeug für die Diagnose</p>
          </div>
          <Button
            onClick={() => setShowAddDialog(true)}
            style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Fahrzeug hinzufügen
          </Button>
        </div>

        {loading ? (
          <p style={{ color: '#94A3B8' }}>Laden...</p>
        ) : vehicles.length === 0 ? (
          <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <CardContent className="p-8 text-center">
              <Car className="w-16 h-16 mx-auto mb-4" style={{ color: '#94A3B8' }} />
              <p className="mb-4" style={{ color: '#94A3B8' }}>Noch keine Fahrzeuge vorhanden</p>
              <Button
                onClick={() => setShowAddDialog(true)}
                style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
              >
                Erstes Fahrzeug hinzufügen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {vehicles.map((vehicle) => (
              <Card
                key={vehicle.id}
                className="cursor-pointer hover:border-blue-500 transition-colors"
                style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}
                onClick={() => handleSelectVehicle(vehicle.id)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2" style={{ color: '#E6EDF3' }}>
                        {vehicle.brand} {vehicle.model}
                      </h3>
                      <p style={{ color: '#94A3B8' }}>{vehicle.engine}</p>
                      <p className="text-sm" style={{ color: '#94A3B8' }}>Baujahr: {vehicle.year}</p>
                      {vehicle.vin && (
                        <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>VIN: {vehicle.vin}</p>
                      )}
                    </div>
                    <ArrowRight className="w-6 h-6" style={{ color: '#7CD4FD' }} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#E6EDF3' }}>Neues Fahrzeug hinzufügen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label style={{ color: '#E6EDF3' }}>Marke *</Label>
                <Input
                  value={newVehicle.brand}
                  onChange={(e) => setNewVehicle({ ...newVehicle, brand: e.target.value })}
                  placeholder="z.B. BMW, Mercedes, VW"
                  className="mt-1"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>Modell *</Label>
                <Input
                  value={newVehicle.model}
                  onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                  placeholder="z.B. 320i, E-Klasse, Golf"
                  className="mt-1"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>Motor *</Label>
                <Input
                  value={newVehicle.engine}
                  onChange={(e) => setNewVehicle({ ...newVehicle, engine: e.target.value })}
                  placeholder="z.B. 2.0 TDI, 3.0 V6"
                  className="mt-1"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>Baujahr *</Label>
                <Input
                  type="number"
                  value={newVehicle.year}
                  onChange={(e) => setNewVehicle({ ...newVehicle, year: parseInt(e.target.value) })}
                  className="mt-1"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>VIN (optional)</Label>
                <Input
                  value={newVehicle.vin}
                  onChange={(e) => setNewVehicle({ ...newVehicle, vin: e.target.value })}
                  placeholder="Fahrzeug-Identifikationsnummer"
                  className="mt-1"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>
              <div>
                <Label style={{ color: '#E6EDF3' }}>Kennzeichen</Label>
                <Input
                  value={newVehicle.license_plate}
                  onChange={(e) => setNewVehicle({ ...newVehicle, license_plate: e.target.value })}
                  placeholder="z.B. M-AB 1234"
                  className="mt-1"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
              </div>
              <Button
                onClick={handleAddVehicle}
                disabled={!newVehicle.brand || !newVehicle.model || !newVehicle.engine}
                className="w-full"
                style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
              >
                Fahrzeug hinzufügen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};